import { useTranslation } from 'react-i18next';
import { theme } from 'antd';
import type { OrderView } from '@entities/order';
import { UserCell } from '@entities/user-directory';
import { Avatar } from '@shared/ui';

interface OrderHistoryProps {
  order: OrderView;
}

export function OrderHistory({ order }: OrderHistoryProps) {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const { history, buyerId, buyer, producer } = order;

  if (history.length === 0) {
    return <p className="text-sm opacity-70">{t('orders.history.empty')}</p>;
  }

  const producerInitials = (producer?.companyName || '?').trim().slice(0, 2).toUpperCase();

  return (
    <ol style={{ display: 'flex', flexDirection: 'column', gap: 12, margin: 0, padding: 0, listStyle: 'none' }}>
      {history.map((entry, idx) => {
        const isBuyer = entry.actor === buyerId;
        return (
          <li
            key={`${entry.at}-${idx}`}
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
              padding: 12,
              borderRadius: token.borderRadius,
              background: token.colorFillQuaternary,
              border: `1px solid ${token.colorSplit}`,
            }}
          >
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className="font-medium">
                {entry.from ? `${t(`orders.status.${entry.from}`)} → ` : ''}
                {t(`orders.status.${entry.to}`)}
              </span>
              <span className="text-xs opacity-70">{new Date(entry.at).toLocaleString()}</span>
            </div>
            {entry.note ? <p className="text-sm opacity-70" style={{ margin: 0 }}>{entry.note}</p> : null}
            <span className="flex items-center gap-2 text-xs opacity-70">
              {t('orders.history.by')}
              {isBuyer ? (
                <UserCell userId={buyerId} user={buyer} size="xs" />
              ) : producer ? (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <Avatar
                    seed={producer.id}
                    initials={producerInitials}
                    src={producer.logo?.url ?? undefined}
                    size="xs"
                  />
                  <span style={{ fontSize: 12 }}>{producer.companyName}</span>
                </span>
              ) : (
                <UserCell userId={entry.actor} size="xs" />
              )}
            </span>
          </li>
        );
      })}
    </ol>
  );
}
